import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { MessageSquare, Send, X, Users } from "lucide-react";

export default function TeamChat({ hospitalId, members }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const scrollRef = useRef(null);
  const pollRef = useRef(null);
  const lastCountRef = useRef(0);

  const loadMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chat/${hospitalId}`);
      const msgs = res.data;
      if (!open && msgs.length > lastCountRef.current) {
        setUnread((prev) => prev + msgs.length - lastCountRef.current);
      }
      lastCountRef.current = msgs.length;
      setMessages(msgs);
    } catch {
      // Silent: polling error, will retry automatically
    }
  }, [hospitalId, open]);

  // Initial load + polling
  useEffect(() => {
    loadMessages();
    pollRef.current = setInterval(loadMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [loadMessages]);

  // Clear unread when opening
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current && open) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setInput("");
    try {
      await axios.post(`${API}/chat/send`, { hospital_id: hospitalId, content: text });
      await loadMessages();
    } catch {
      toast.error("Error sending message");
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Floating button (left side to not conflict with copilot on right)
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="team-chat-toggle-btn"
        className="fixed bottom-20 left-6 z-50 w-14 h-14 rounded-full bg-slate-900 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center group"
      >
        <MessageSquare className="w-6 h-6 group-hover:animate-pulse" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="chat-unread-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-20 left-6 z-50 w-[340px] h-[480px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300"
      data-testid="team-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-900 text-white shrink-0">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" />
          <div>
            <p className="text-sm font-semibold leading-none">{t("teamChat.title")}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{members?.length || 0} {t("teamChat.members")}</p>
          </div>
        </div>
        <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-white/10 transition-colors" data-testid="team-chat-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2" data-testid="team-chat-messages">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageSquare className="w-8 h-8 text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">{t("teamChat.empty")}</p>
          </div>
        )}
        {messages.map((msg) => {
          const isMe = msg.user_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] ${isMe ? "" : ""}`}>
                {!isMe && (
                  <p className="text-[10px] font-medium text-slate-400 mb-0.5 ml-1">{msg.user_name}</p>
                )}
                <div
                  className={`rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
                    isMe
                      ? "bg-slate-900 text-white rounded-br-md"
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                  data-testid={`chat-msg-${msg.id}`}
                >
                  {msg.content}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Input */}
      <div className="shrink-0 border-t border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t("teamChat.placeholder")}
            rows={1}
            className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300 placeholder:text-slate-400"
            data-testid="team-chat-input"
          />
          <Button
            size="sm"
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            className="h-9 w-9 rounded-xl bg-slate-900 hover:bg-slate-800 p-0 shrink-0"
            data-testid="team-chat-send-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
